import { Chart, registerables, TooltipItem } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface ExtremeCommutingPieChartProps {
  location: string;
}

const LABEL_ORDER = ['Under 15 min', '15-30 min', '30-45 min', '45-60 min', '60-90 min', 'Over 90 min'];

/** Map ACS commute_time category to display buckets */
function formatCommuteTimeLabel(category: string): string {
  if (!category || category === 'total') return 'Total';
  const normalized = category.toLowerCase().replace(/_/g, ' ');
  if (normalized.includes('90') || normalized.includes('or more')) return 'Over 90 min';
  if (normalized.includes('60')) return '60-90 min';
  if (normalized.includes('45')) return '45-60 min';
  if (normalized.includes('35')) return '30-45 min';
  if (normalized.includes('30')) return '30-45 min';
  if (normalized.includes('25') || normalized.includes('20') || normalized.includes('15')) return '15-30 min';
  if (normalized.includes('10') || normalized.includes('less') || normalized.includes('under') || normalized.includes('5')) {
    return 'Under 15 min';
  }
  return category.replace(/_/g, ' ');
}

type Row = { year: number; commute_time: string; estimate: number };

function percentsByOrderedLabels(rows: Row[]): { year: number | null; pcts: number[] } {
  if (!rows.length) return { year: null, pcts: LABEL_ORDER.map(() => 0) };
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const latestYear = years[years.length - 1];
  const yearRows = rows.filter((r) => r.year === latestYear && r.commute_time && r.commute_time !== 'total');
  const labelToEstimate = new Map<string, number>();
  for (const r of yearRows) {
    const label = formatCommuteTimeLabel(r.commute_time);
    labelToEstimate.set(label, (labelToEstimate.get(label) ?? 0) + (Number(r.estimate) || 0));
  }
  const values = LABEL_ORDER.map((l) => labelToEstimate.get(l) ?? 0);
  const total = values.reduce((a, b) => a + b, 0);
  const pcts = total > 0 ? values.map((v) => (v / total) * 100) : values.map(() => 0);
  return { year: latestYear, pcts };
}

const COLOR_LOCAL = 'rgba(37, 99, 235, 0.85)';
const COLOR_STATE = 'rgba(220, 38, 38, 0.85)';

const ExtremeCommutingPieChart: React.FC<ExtremeCommutingPieChartProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localPcts, setLocalPcts] = useState<number[] | null>(null);
  const [statePcts, setStatePcts] = useState<number[] | null>(null);
  const [localYear, setLocalYear] = useState<number | null>(null);
  const [stateYear, setStateYear] = useState<number | null>(null);
  const [showState, setShowState] = useState(false);

  const fetchRowsForGeofips = async (geofips: number): Promise<Row[]> => {
    const { data: viewData, error: viewError } = await supabaseClient
      .from('extreme_commuting_by_place')
      .select('year, commute_time, estimate')
      .eq('place_geofips', geofips)
      .order('year', { ascending: false });

    if (!viewError && viewData?.length) {
      return viewData as Row[];
    }

    const { data: directData, error: directError } = await supabaseClient
      .from('extreme_commuting')
      .select('year, commute_time, estimate')
      .eq('geofips', geofips)
      .order('year', { ascending: false });

    if (directError || !directData?.length) {
      throw new Error(viewError?.message || directError?.message || 'No extreme commuting data available.');
    }

    return directData as Row[];
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!location) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setLocalPcts(null);
      setStatePcts(null);
      try {
        await supabaseLocationService.initialize();
        const [placeGeofips, californiaGeofips] = await Promise.all([
          supabaseLocationService.getGeofips(location),
          supabaseLocationService.getGeofips('California')
        ]);

        if (!placeGeofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        const localRows = await fetchRowsForGeofips(placeGeofips);
        const { year: yL, pcts: pL } = percentsByOrderedLabels(localRows);
        if (cancelled) return;
        setLocalYear(yL);
        setLocalPcts(pL);

        const isCA = location.trim().toLowerCase() === 'california';
        if (!isCA && californiaGeofips) {
          const caRows = await fetchRowsForGeofips(californiaGeofips);
          const { year: yS, pcts: pS } = percentsByOrderedLabels(caRows);
          if (cancelled) return;
          setStateYear(yS);
          setStatePcts(pS);
          setShowState(true);
        } else {
          setStatePcts(null);
          setShowState(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('ExtremeCommutingPieChart:', err);
          setError(err instanceof Error ? err.message : 'Failed to load commute time data');
          setLocalPcts(null);
          setStatePcts(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (!canvasRef.current || !localPcts) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const hasState = showState && statePcts && statePcts.some((v) => v > 0);
    const datasets = [
      {
        label: localYear != null ? `${location} (${localYear})` : location,
        data: localPcts,
        backgroundColor: COLOR_LOCAL,
        borderRadius: 4,
        maxBarThickness: 36
      }
    ];
    if (hasState && statePcts) {
      datasets.push({
        label: stateYear != null ? `California (${stateYear})` : 'California',
        data: statePcts,
        backgroundColor: COLOR_STATE,
        borderRadius: 4,
        maxBarThickness: 36
      });
    }

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: LABEL_ORDER,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
          legend: {
            position: 'top' as const,
            labels: { usePointStyle: true, padding: 16 }
          },
          tooltip: {
            callbacks: {
              label: (item: TooltipItem<'bar'>) => {
                const v = item.parsed.y;
                if (v == null || Number.isNaN(v)) return '';
                return `${item.dataset.label}: ${Number(v).toFixed(1)}%`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Percent of workers' }
          },
          x: {
            title: { display: true, text: 'One-way commute time' },
            ticks: { maxRotation: 45, minRotation: 0 }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [location, localPcts, statePcts, localYear, stateYear, showState]);

  if (loading) {
    return <div className="placeholder-chart">Loading commute time data…</div>;
  }

  if (error) {
    return (
      <div className="placeholder-chart" style={{ color: '#b91c1c' }}>
        {error}
      </div>
    );
  }

  if (!localPcts) {
    return <div className="placeholder-chart">No data available</div>;
  }

  return (
    <div>
      <h3 style={{ marginBottom: '12px' }}>Commute time to work</h3>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#475569' }}>
        {showState && statePcts
          ? 'Share of workers by commute length. Blue = selected place, red = California average (latest year each).'
          : 'Share of workers by commute length (latest year).'}
      </p>
      <div style={{ height: 420 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default ExtremeCommutingPieChart;
